import { Directive, OnDestroy, OnInit, TemplateRef, ViewContainerRef, Renderer2 } from '@angular/core';
import { filter, map, startWith, Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { EmbeddedService } from '../services/embedded/embedded.service';

@Directive({
	selector: '[embedded-view]',
})
export class EmbeddedDirective implements OnInit, OnDestroy {
	private routerSubscription: Subscription | undefined;
	private styleElement: HTMLStyleElement | null = null;

	constructor(
		private readonly templateRef: TemplateRef<any>,
		private readonly viewContainer: ViewContainerRef,
		private readonly router: Router,
		private embeddedService: EmbeddedService,
		private readonly renderer: Renderer2,
	) {}

	ngOnInit(): void {
		this.routerSubscription = this.router.events
			.pipe(
				startWith(null),
				filter((event) => event === null || event instanceof NavigationEnd),
				map(() => this.embeddedService.getLeafRoute(this.router.routerState.root)),
			)
			.subscribe((route) => {
				const shouldHide = route.snapshot.data['embedded'] === true;
				this.updateView(shouldHide);
				this.updateStyles(shouldHide);
			});
	}

	ngOnDestroy(): void {
		this.routerSubscription?.unsubscribe();
		if (this.styleElement) {
			this.renderer.removeChild(document.head, this.styleElement);
			this.styleElement = null;
		}
	}

	private updateView(shouldHide: boolean): void {
		const isViewCreated = this.viewContainer.length > 0;
		if (shouldHide && isViewCreated) {
			this.viewContainer.clear();
		} else if (!shouldHide && !isViewCreated) {
			this.viewContainer.createEmbeddedView(this.templateRef);
		}
	}

	private updateStyles(shouldHide: boolean): void {
		if (!this.styleElement) {
			this.styleElement = this.renderer.createElement('style');
			this.renderer.setProperty(this.styleElement, 'type', 'text/css');
			this.renderer.setAttribute(this.styleElement, 'data-embedded-directive', '');
			this.renderer.appendChild(document.head, this.styleElement);
		}

		const outputContainerOverrides = shouldHide
			? '.outputs-container { top: var(--space-sgnl-space-16, 1rem) !important; }'
			: '.outputs-container { top: 70px !important; }';

		const demoBoddyOverrides = shouldHide
			? '.demo-body { padding: 0 !important; }'
			: '.demo-body { padding: var(--space-sgnl-space-16, 1rem) !important; }';

		const componentOverrides = demoBoddyOverrides + outputContainerOverrides;

		this.renderer.setProperty(this.styleElement, 'textContent', componentOverrides);
	}
}
